import get from "lodash/get";
import omit from "lodash/omit";

import {ServiceField} from "@/lib/constants/service.ts";
import {
  CheckTypeDisplay,
  MonitoringServiceType,
  Service,
  ServiceDefinition,
  ServiceFieldDefinition,
  ThirdPartyMonitoringType,
} from "@/lib/types/checks.ts";
import {MonitoringGroup, MonitoringGroupType} from "@/lib/types/monitoring.ts";
import {advancedFields, baseSections, QUERY} from "@/pages/checks/constants.ts";
import {CreateCheckFormValues} from "@/pages/checks/update/CreateCheckForm.types.ts";

export const cleanCheckFormInput = (data: CreateCheckFormValues) => {
  const extraData: Record<string, unknown> = {
    escalations: data.escalations.filter((e) => e.contact_groups.length > 0),
  };
  const skipContactGroups = get(data, ServiceField.GROUP_UPTIME_PERCENT_CALCULATION) === "AVERAGE";
  const singleCheckResponseTime = get(data, "group_response_time_single_check.id", null);
  const thirdPartyGroup = data[ServiceField.THIRD_PARTY_GROUP];
  const thirdPartyMonitoringType = data[ServiceField.THIRD_PARTY_MONITORING_TYPE];
  const thirdPartyComponents =
    thirdPartyMonitoringType === ThirdPartyMonitoringType.ALL
      ? []
      : data[ServiceField.THIRD_PARTY_COMPONENTS]?.map((component) => ({
          // The backend builds the Check name from sub_title: send the name the customer sees.
          ...omit(component, ["display_title", "display_sub_title"]),
          sub_title: component.display_sub_title || component.sub_title,
          sub_components:
            component.sub_component_monitoring_type === ThirdPartyMonitoringType.ALL
              ? []
              : component.sub_components?.map(({id, sub_title, display_sub_title}) => ({
                  id,
                  sub_title: display_sub_title || sub_title,
                })),
        }));
  return omit({
    ...data,
    ...extraData,
    ...(singleCheckResponseTime ? {group_response_time_single_check: singleCheckResponseTime} : {}),
    ...(thirdPartyGroup
      ? {[ServiceField.THIRD_PARTY_GROUP]: {id: thirdPartyGroup.id, name: thirdPartyGroup.group}}
      : {}),
    ...(data[ServiceField.THIRD_PARTY_COMPONENTS] ? {[ServiceField.THIRD_PARTY_COMPONENTS]: thirdPartyComponents} : {}),
    contact_groups: skipContactGroups ? [] : data.contact_groups,
  });
};

export const hasSnippet = (serviceType: MonitoringServiceType): boolean => {
  return [MonitoringServiceType.RUM2, MonitoringServiceType.WEBHOOK, MonitoringServiceType.HEARTBEAT].includes(
    serviceType
  );
};

export const hasVault = (serviceType: MonitoringServiceType): boolean => {
  return [
    MonitoringServiceType.HTTP,
    MonitoringServiceType.FTP,
    MonitoringServiceType.SFTP,
    MonitoringServiceType.SMTP,
    MonitoringServiceType.API,
    MonitoringServiceType.TRANSACTION,
    MonitoringServiceType.PAGESPEED,
  ].includes(serviceType);
};

export const hasCheckSteps = (serviceType: MonitoringServiceType): boolean => {
  return [MonitoringServiceType.API, MonitoringServiceType.TRANSACTION, MonitoringServiceType.PAGESPEED].includes(
    serviceType
  );
};

// the steps editor stores the script as a serialized JSON array; anything else it may have left
// behind is malformed rather than empty, so it goes to the backend's own script validation
export const isEmptyCheckScript = (script?: string): boolean => {
  if (!script) {
    return true;
  }
  try {
    const steps: unknown = JSON.parse(script);
    return Array.isArray(steps) && steps.length === 0;
  } catch {
    return false;
  }
};

export const hasTestRunnerOnSensitivity = (serviceType: MonitoringServiceType): boolean => {
  return [
    MonitoringServiceType.HTTP,
    MonitoringServiceType.SSL_CERT,
    MonitoringServiceType.TCP,
    MonitoringServiceType.UDP,
    MonitoringServiceType.POP,
    MonitoringServiceType.IMAP,
    MonitoringServiceType.SMTP,
  ].includes(serviceType);
};

export const getCheckCreatedMessage = (serviceType: MonitoringServiceType, forceInterval?: number) => {
  const label = CheckTypeDisplay[serviceType];
  let message = `Your ${label} Check will commence monitoring within 5 minutes.`;
  if (serviceType === MonitoringServiceType.GROUP) {
    message = `Your ${label} Check is ready to monitor associated Checks.`;
  } else if (forceInterval === 0 || hasSnippet(serviceType)) {
    message = `Your ${label} Check is ready to receive data.`;
  } else if (forceInterval === 60) {
    message = `Your ${label} Check will commence monitoring hourly.`;
  } else if (forceInterval) {
    message = `Your ${label} Check will commence monitoring daily.`;
  }
  return message;
};

/** Such a Group Check aggregates nothing, so its filtered Checks list would always be empty. */
export const isEmptyGroupCheck = (
  service: Pick<Service, "monitoring_service_type" | "group_check_services" | "group_check_tag_ids">
): boolean =>
  service.monitoring_service_type === MonitoringServiceType.GROUP &&
  !service.group_check_services?.length &&
  !service.group_check_tag_ids?.length;

export const getCheckDetailUrl = (
  id: number,
  serviceType: MonitoringServiceType,
  isEmptyGroup: boolean = false
): string => {
  if (serviceType === MonitoringServiceType.PAGESPEED) {
    return `/pagespeed/report/${id}`;
  }
  if (serviceType === MonitoringServiceType.RUM2) {
    return `/rum/v2/detail/${id}`;
  }
  if (serviceType === MonitoringServiceType.GROUP) {
    return isEmptyGroup
      ? `/checks/${id}/edit?${QUERY.TAB}=${baseSections.aggregationnalerts.id}`
      : `/checks/group/${id}`;
  }
  return `/checks/${id}`;
};

export const isLocationFieldVisible = (
  monitoringGroups: MonitoringGroup[],
  forceMonitoringGroup?: MonitoringGroupType
): boolean => {
  const hasPrivateLocations = monitoringGroups.some((mg) => mg.is_private);
  const isLocationHidden =
    forceMonitoringGroup === MonitoringGroupType.DISABLED ||
    (forceMonitoringGroup === MonitoringGroupType.SINGLE_LOCATION_CHECKS && !hasPrivateLocations);

  return !isLocationHidden;
};

const getFieldTabId = (serviceDef: ServiceDefinition, field: string): string | undefined =>
  Object.values(baseSections).find(({fields}) => fields.includes(field as ServiceField))?.id ??
  advancedFields[field]?.[serviceDef.service_type] ??
  advancedFields[field]?.default;

// General fields required in JSX, outside the service definition
export const tabHasRequiredFields = (serviceDef: ServiceDefinition, tab: string): boolean =>
  tab === baseSections.general.id ||
  serviceDef.fields.some(
    ({field, visible, optional}) => visible && !optional && getFieldTabId(serviceDef, field) === tab
  );

export const buildRumSnippet = (
  template: string,
  uuid: string,
  externalDomains?: string,
  isAjaxDisabled?: boolean
): string => {
  const domains = (externalDomains ?? "")
    .split("\n")
    .map((domain) => domain.trim())
    .filter(Boolean)
    .map((domain) => `"${domain}"`);
  const extd = domains.length ? `w._uptime_rum2.ext=[${domains.join(",")}];` : "";
  const ajax = isAjaxDisabled ? "w._uptime_rum2.ajax=false;" : "";
  return template.replace("##UUID##", uuid).replace("###EXTD###", extd).replace("###AJAX###", ajax);
};

export const calculateServiceIncrement = (serviceType: MonitoringServiceType, hasTraceroute: boolean): number => {
  if (serviceType === MonitoringServiceType.NETWORK && hasTraceroute) {
    return 2;
  }
  return 1;
};

export const fieldIsEnabled = (
  field: ServiceFieldDefinition | undefined,
  userHasFeature: (name: string) => unknown
): boolean => !!field && (!field.feature_flag || !!userHasFeature(field.feature_flag));

export const omitDisabledFields = <T extends object>(
  data: T,
  fields: ServiceFieldDefinition[],
  userHasFeature: (name: string) => unknown
): T =>
  omit(
    data,
    fields.filter((field) => !fieldIsEnabled(field, userHasFeature)).map((field) => field.field)
  ) as T;

export const sectionHasChanges = (
  fields: ServiceFieldDefinition[],
  values: Record<string, unknown>,
  errors: Record<string, unknown>
): boolean =>
  fields.some(
    (field) =>
      field.field in errors ||
      (values[field.field] != null && String(values[field.field]) !== String(field.default ?? ""))
  );
